import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedIcon from '@mui/icons-material/Verified';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import img1 from './images/hubungi.jpg';
import juniusProfile from './images/junius-profile.jpeg';
import contactSeoImage from './images/kv-pc.jpg';
import PageSeo, {
  SITE,
  absoluteUrl,
  breadcrumbJsonLd,
  keywordSets,
  localBusinessJsonLd,
} from './PageSeo';

const waLink =
  'https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20ingin%20info%20harga%2C%20promo%2C%20dan%20test%20drive%20BYD%20di%20BSD%20Serpong';

const mapLink =
  'https://www.google.com/maps/place/BYD+Arista+BSD+City/@-6.2816255,106.6342924,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69fb861088715d:0xcd66cbac6d05d9d0!8m2!3d-6.2816308!4d106.6368673!16s%2Fg%2F11svrps209';

const faqItems = [
  {
    question: 'Apa cara tercepat untuk menghubungi Junius?',
    answer:
      'WhatsApp adalah kanal tercepat untuk tanya harga, promo, stok unit, simulasi pembelian, dan jadwal test drive BYD di BSD Serpong.',
  },
  {
    question:
      'Apakah BYD BSD Serpong melayani konsumen dari Tangerang dan Gading Serpong?',
    answer:
      'Ya. Showroom BYD BSD Serpong melayani konsumen dari BSD Serpong, Gading Serpong, Tangerang, Tangerang Selatan, Alam Sutera, dan area sekitarnya.',
  },
  {
    question: 'Bisa booking test drive lewat halaman ini?',
    answer:
      'Bisa. Hubungi Junius lewat WhatsApp atau telepon untuk mengatur jadwal test drive sesuai slot dan ketersediaan unit.',
  },
  {
    question:
      'Apakah saya bisa tanya promo dan simulasi pembelian sebelum datang ke showroom?',
    answer:
      'Bisa. Anda bisa minta info harga OTR, promo berjalan, simulasi pembelian, hingga ketersediaan warna atau unit sebelum menjadwalkan kunjungan.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  inLanguage: 'id-ID',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const contactPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Kontak BYD BSD Serpong',
  url: absoluteUrl('/contact'),
  description:
    'Halaman kontak resmi BYD BSD Serpong untuk menghubungi Junius via WhatsApp, cek lokasi showroom, jam operasional, dan booking test drive.',
  inLanguage: 'id-ID',
  about: {
    '@id': `${SITE.url}/#dealer`,
  },
  mainEntity: {
    '@id': `${SITE.url}/#dealer`,
  },
};

const personJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Junius',
  jobTitle: 'Konsultan Penjualan BYD BSD Serpong',
  description:
    'Konsultan penjualan BYD BSD Serpong untuk konsultasi harga, promo, stok unit, dan booking test drive.',
  telephone: SITE.phone,
  sameAs: [SITE.instagram, SITE.whatsapp],
  image: absoluteUrl(juniusProfile),
  worksFor: {
    '@id': `${SITE.url}/#dealer`,
  },
};

function Contact() {
  return (
    <>
      <PageSeo
        title='Kontak BYD BSD Serpong | WhatsApp Junius & Lokasi Showroom'
        description='Hubungi Junius di dealer resmi BYD BSD Serpong untuk cek harga, promo, stok unit, lokasi showroom, dan booking test drive area BSD, Tangerang, dan Gading Serpong.'
        keywords={[
          ...keywordSets.localDealer,
          'kontak BYD BSD Serpong',
          'hubungi BYD BSD Serpong',
          'WhatsApp Junius BYD',
          'lokasi showroom BYD BSD',
          'dealer BYD Tangerang Selatan',
          'test drive BYD BSD Serpong',
          'booking test drive BYD BSD',
        ]}
        path='/contact'
        image={contactSeoImage}
        jsonLd={[
          localBusinessJsonLd(),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Kontak BYD BSD Serpong', path: '/contact' },
          ]),
          contactPageJsonLd,
          personJsonLd,
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ backgroundColor: '#f4f6fb' }}>
        <HeroSection />
        <DetailsSection />
        <LocalSupportSection />
        <FaqSection />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection() {
  const stats = [
    { label: 'Konsultan penjualan', value: 'Junius' },
    { label: 'Area layanan', value: 'BSD, Gading Serpong, Tangerang' },
    { label: 'Jam showroom', value: '09.00 - 18.00' },
  ];

  return (
    <Box
      component='section'
      aria-labelledby='contact-hero-title'
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at top left, rgba(46,103,255,0.28), transparent 38%), linear-gradient(180deg, #111827 0%, #0f172a 100%)',
        pt: { xs: 12, md: 14 },
        pb: { xs: 6, md: 8 },
        px: { xs: 2, md: 6 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ color: '#fff' }}>
          <Chip
            label='Dealer Resmi BYD BSD Serpong'
            sx={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.14)',
              fontWeight: 800,
              letterSpacing: 0.2,
            }}
          />
          <Typography
            id='contact-hero-title'
            component='h1'
            sx={{
              mt: 2,
              fontSize: { xs: '2.4rem', md: '4.4rem' },
              fontWeight: 900,
              lineHeight: 0.98,
              maxWidth: 760,
            }}
          >
            Kontak Junius, Konsultan Penjualan BYD BSD Serpong
          </Typography>
          <Typography
            sx={{
              mt: 2,
              maxWidth: 760,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.08rem' },
            }}
          >
            Butuh info harga OTR, promo yang sedang berjalan, stok unit, atau
            jadwal test drive? Hubungi Junius di dealer resmi BYD BSD Serpong.
            Kami melayani konsumen dari BSD Serpong, Gading Serpong,
            Tangerang, Tangerang Selatan, dan area sekitarnya.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {['BSD Serpong', 'Gading Serpong', 'Harga & Promo', 'Test Drive'].map(
              (item) => (
                <Box
                  key={item}
                  sx={{
                    px: 1.4,
                    py: 0.8,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '0.92rem',
                  }}
                >
                  {item}
                </Box>
              ),
            )}
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1,
              maxWidth: 860,
            }}
          >
            {stats.map((item) => (
              <Box
                key={item.label}
                sx={{
                  borderRadius: '16px',
                  px: 2,
                  py: 1.7,
                  border: '1px solid rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                }}
              >
                <Typography sx={{ fontSize: '1.02rem', fontWeight: 900 }}>
                  {item.value}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.4,
                    color: 'rgba(255,255,255,0.68)',
                    fontSize: '0.84rem',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Button
              href={waLink}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              startIcon={<WhatsAppIcon />}
              sx={ctaPrimary}
            >
              Chat WhatsApp Junius
            </Button>
            <Button
              href={mapLink}
              target='_blank'
              rel='noopener noreferrer'
              variant='outlined'
              startIcon={<LocationOnIcon />}
              sx={ctaDark}
            >
              Buka Google Maps
            </Button>
            <Button
              href='tel:+6281286864825'
              variant='outlined'
              startIcon={<PhoneIcon />}
              sx={ctaDark}
            >
              Telepon Dealer
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            justifyItems: 'stretch',
          }}
        >
          <Box
            sx={{
              borderRadius: '26px',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 28px 60px rgba(0,0,0,0.28)',
              backgroundColor: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              component='img'
              src={img1}
              alt='Showroom BYD BSD Serpong'
              sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <Box sx={{ p: 2.3, color: '#fff' }}>
              <Typography sx={{ fontWeight: 900, fontSize: '1.12rem' }}>
                Showroom BYD BSD Serpong
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  color: 'rgba(255,255,255,0.74)',
                  lineHeight: 1.7,
                }}
              >
                Datang ke showroom untuk lihat unit lebih dekat, cek pilihan
                warna, bandingkan model, dan atur test drive langsung bersama
                tim BYD BSD Serpong.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: '24px',
              backgroundColor: '#fff',
              p: 2.2,
              boxShadow: '0 22px 48px rgba(0,0,0,0.18)',
              display: 'grid',
              gridTemplateColumns: '108px 1fr',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Box
              component='img'
              src={juniusProfile}
              alt='Junius sales consultant BYD BSD Serpong'
              sx={{
                width: '100%',
                height: 132,
                objectFit: 'cover',
                objectPosition: 'center top',
                borderRadius: '18px',
                display: 'block',
              }}
            />
            <Box>
              <Typography
                sx={{
                  color: '#667085',
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Konsultan Penjualan BYD
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: '1.8rem',
                  fontWeight: 900,
                  color: '#111827',
                  lineHeight: 1.05,
                }}
              >
                Junius
              </Typography>
              <Chip
                icon={<VerifiedIcon />}
                label='Tim Dealer Resmi'
                size='small'
                sx={{
                  mt: 1,
                  backgroundColor: '#edf4ff',
                  color: '#1747c7',
                  fontWeight: 800,
                }}
              />
              <Typography
                sx={{
                  mt: 1.2,
                  color: '#4b5563',
                  lineHeight: 1.7,
                  fontSize: '0.96rem',
                }}
              >
                Siap membantu info harga OTR, promo aktif, ketersediaan warna,
                sampai proses booking test drive.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function DetailsSection() {
  const contactRows = [
    {
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
      label: 'WhatsApp',
      value: '0812 8686 4825',
      href: waLink,
      helper:
        'Kanal tercepat untuk cek harga, promo, stok unit, dan jadwal test drive.',
    },
    {
      icon: <PhoneIcon sx={{ color: '#111827' }} />,
      label: 'Telepon',
      value: '+62 812-8686-4825',
      href: 'tel:+6281286864825',
      helper: 'Ideal untuk konfirmasi cepat sebelum datang ke showroom.',
    },
    {
      icon: <InstagramIcon sx={{ color: '#C13584' }} />,
      label: 'Instagram',
      value: '@bydbsdserpong',
      href: 'https://www.instagram.com/bydbsdserpong/',
      helper: 'Lihat update promo, unit display, dan aktivitas showroom terbaru.',
    },
    {
      icon: <BusinessIcon sx={{ color: '#475467' }} />,
      label: 'Showroom',
      value: 'BYD Arista BSD City',
      href: mapLink,
      helper: 'Buka rute ke dealer resmi BYD BSD Serpong via Google Maps.',
    },
  ];

  const quickLinks = [
    {
      title: 'Lihat harga dan promo BYD',
      description:
        'Cek kisaran harga OTR dan promo terbaru untuk model BYD yang tersedia.',
      path: '/pricing',
      ctaLabel: 'Buka halaman harga',
    },
    {
      title: 'Jelajahi BYD Atto 1',
      description:
        'Lihat spesifikasi, pilihan warna, dan fitur utama BYD Atto 1.',
      path: '/jelajahi-atto1',
      ctaLabel: 'Lihat detail Atto 1',
    },
    {
      title: 'Lihat galeri showroom',
      description:
        'Lihat suasana showroom dan unit display sebelum Anda datang.',
      path: '/gallery',
      ctaLabel: 'Buka galeri',
    },
  ];

  return (
    <Box
      component='section'
      aria-labelledby='contact-detail-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1.05fr 0.95fr' },
          gap: { xs: 3, lg: 4 },
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            borderRadius: '24px',
            backgroundColor: '#fff',
            p: { xs: 2.5, md: 3.2 },
            boxShadow: '0 20px 42px rgba(16,24,40,0.08)',
            border: '1px solid rgba(17,24,39,0.06)',
          }}
        >
          <Typography
            id='contact-detail-title'
            component='h2'
            sx={{
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.02,
            }}
          >
            Pilih cara menghubungi kami
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: '#4b5563',
              lineHeight: 1.75,
              maxWidth: 760,
            }}
          >
            Pilih kanal yang paling nyaman. WhatsApp biasanya paling cepat
            untuk cek harga, promo, stok unit, dan jadwal test drive. Jika
            ingin datang langsung, cek alamat showroom serta jam operasional di
            bawah.
          </Typography>

          <Box sx={{ mt: 3, display: 'grid', gap: 1.4 }}>
            {contactRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
          </Box>

          <Divider sx={{ my: 2.4 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box
              sx={{
                p: 2.2,
                borderRadius: '18px',
                backgroundColor: '#f7f9fc',
                border: '1px solid rgba(17,24,39,0.06)',
              }}
            >
              <Typography sx={subtleLabel}>Alamat showroom</Typography>
              <Typography sx={infoText}>
                BSD City Serpong Kavling De Park, Jl. BSD Raya Utama Lot 1,
                Lengkong Kulon, Kec. Pagedangan, Kabupaten Tangerang, Banten
                15331
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2.2,
                borderRadius: '18px',
                backgroundColor: '#f7f9fc',
                border: '1px solid rgba(17,24,39,0.06)',
              }}
            >
              <Typography sx={subtleLabel}>Jam operasional</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                <AccessTimeIcon sx={{ color: '#1f5fd1', fontSize: '1.1rem' }} />
                <Typography sx={infoText}>Senin - Minggu, 09.00 - 18.00</Typography>
              </Box>
              <Typography sx={{ mt: 1, color: '#667085', lineHeight: 1.7 }}>
                Sebaiknya hubungi kami lebih dulu agar unit display atau jadwal
                test drive bisa disiapkan.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 2.2 }}>
          <Box
            sx={{
              borderRadius: '24px',
              backgroundColor: '#fff',
              p: { xs: 2.5, md: 3 },
              boxShadow: '0 20px 42px rgba(16,24,40,0.08)',
              border: '1px solid rgba(17,24,39,0.06)',
            }}
          >
            <Typography
              sx={{
                fontSize: '1.45rem',
                fontWeight: 900,
                color: '#111827',
              }}
            >
              Area layanan
            </Typography>
            <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.75 }}>
              Showroom ini paling praktis untuk konsumen di BSD Serpong,
              Gading Serpong, Tangerang, Tangerang Selatan, Alam Sutera, dan
              area sekitarnya.
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {[
                'BSD Serpong',
                'Tangerang',
                'Tangerang Selatan',
                'Gading Serpong',
                'Alam Sutera',
              ].map((area) => (
                <Chip
                  key={area}
                  label={area}
                  sx={{
                    backgroundColor: '#edf4ff',
                    color: '#1f5fd1',
                    fontWeight: 800,
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: '24px',
              backgroundColor: '#fff',
              p: { xs: 2.5, md: 3 },
              boxShadow: '0 20px 42px rgba(16,24,40,0.08)',
              border: '1px solid rgba(17,24,39,0.06)',
            }}
          >
            <Typography
              sx={{
                fontSize: '1.45rem',
                fontWeight: 900,
                color: '#111827',
              }}
            >
              Informasi yang sering dicari
            </Typography>
            <Box sx={{ mt: 2, display: 'grid', gap: 1.2 }}>
              {quickLinks.map((item) => (
                <QuickLinkCard key={item.path} {...item} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function LocalSupportSection() {
  const items = [
    {
      title: 'Harga dan promo',
      text: 'Tanya harga OTR, promo yang sedang berjalan, simulasi pembelian, dan ketersediaan unit langsung ke Junius.',
    },
    {
      title: 'Booking test drive',
      text: 'Atur jadwal test drive BYD di BSD Serpong sesuai model yang ingin dicoba dan slot yang tersedia.',
    },
    {
      title: 'Kunjungan showroom',
      text: 'Konfirmasi kedatangan, cek unit display, atau minta panduan rute ke showroom sebelum berangkat.',
    },
  ];

  return (
    <Box
      component='section'
      aria-labelledby='contact-local-title'
      sx={{
        px: { xs: 2, md: 6 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          borderRadius: '28px',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, #102b8c 0%, #2147c6 54%, #3c68ff 100%)',
          color: '#fff',
          px: { xs: 2.5, md: 4 },
          py: { xs: 3.5, md: 4.5 },
        }}
      >
        <Typography
          id='contact-local-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            lineHeight: 1.02,
            maxWidth: 760,
          }}
        >
          Yang bisa kami bantu
        </Typography>
        <Typography
          sx={{
            mt: 1.4,
            maxWidth: 760,
            color: 'rgba(255,255,255,0.82)',
            lineHeight: 1.8,
          }}
        >
          Halaman ini dibuat untuk memudahkan Anda menghubungi dealer BYD BSD
          Serpong sebelum datang ke showroom atau sebelum booking unit.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {items.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.3,
                borderRadius: '20px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.16)',
              }}
            >
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900 }}>
                {item.title}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: 'rgba(255,255,255,0.82)',
                  lineHeight: 1.75,
                }}
              >
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection() {
  return (
    <Box
      component='section'
      aria-labelledby='contact-faq-title'
      sx={{
        px: { xs: 2, md: 6 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          borderRadius: '24px',
          backgroundColor: '#fff',
          p: { xs: 2.5, md: 3.2 },
          boxShadow: '0 20px 42px rgba(16,24,40,0.08)',
          border: '1px solid rgba(17,24,39,0.06)',
        }}
      >
        <Typography
          id='contact-faq-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.02,
          }}
        >
          Pertanyaan yang sering ditanyakan
        </Typography>
        <Box sx={{ mt: 2.5, display: 'grid', gap: 1.3 }}>
          {faqItems.map((item) => (
            <Box
              key={item.question}
              component='details'
              sx={{
                p: 2,
                borderRadius: '18px',
                backgroundColor: '#f7f9fc',
                border: '1px solid rgba(17,24,39,0.06)',
                '& summary': {
                  cursor: 'pointer',
                  fontWeight: 900,
                  color: '#111827',
                },
              }}
            >
              <Box component='summary'>{item.question}</Box>
              <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.75 }}>
                {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box
      component='section'
      aria-labelledby='contact-cta-title'
      sx={{
        px: { xs: 2, md: 6 },
        pb: { xs: 8, md: 10 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1120,
          mx: 'auto',
          borderRadius: '28px',
          backgroundColor: '#111827',
          color: '#fff',
          px: { xs: 2.5, md: 4 },
          py: { xs: 3.5, md: 4.5 },
          textAlign: 'center',
        }}
      >
        <Typography
          id='contact-cta-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            lineHeight: 1.02,
          }}
        >
          Siap konsultasi harga atau booking test drive?
        </Typography>
        <Typography
          sx={{
            mt: 1.6,
            maxWidth: 760,
            mx: 'auto',
            color: 'rgba(255,255,255,0.78)',
            lineHeight: 1.8,
          }}
        >
          Hubungi Junius sekarang untuk cek promo, stok unit, simulasi
          pembelian, dan jadwal test drive BYD di BSD Serpong.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            href={waLink}
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            startIcon={<WhatsAppIcon />}
            sx={ctaPrimary}
          >
            Chat WhatsApp Junius
          </Button>
          <Button
            component={RouterLink}
            to='/pricing'
            variant='outlined'
            sx={ctaDark}
          >
            Lihat Harga Mobil BYD
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function InfoRow({ icon, label, value, href, helper }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr',
        gap: 1.5,
        alignItems: 'start',
        p: 1.8,
        borderRadius: '18px',
        backgroundColor: '#f7f9fc',
        border: '1px solid rgba(17,24,39,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.2 }}>{icon}</Box>
      <Box>
        <Typography sx={subtleLabel}>{label}</Typography>
        <Typography
          component='a'
          href={href}
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          sx={{
            mt: 0.5,
            display: 'inline-block',
            fontSize: '1.06rem',
            fontWeight: 800,
            color: '#111827',
            textDecoration: 'none',
            '&:hover': { color: '#1f5fd1' },
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ mt: 0.6, color: '#667085', lineHeight: 1.65 }}>
          {helper}
        </Typography>
      </Box>
    </Box>
  );
}

function QuickLinkCard({ title, description, path, ctaLabel }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '18px',
        backgroundColor: '#f7f9fc',
        border: '1px solid rgba(17,24,39,0.06)',
      }}
    >
      <Typography sx={{ fontWeight: 900, color: '#111827' }}>{title}</Typography>
      <Typography sx={{ mt: 0.8, color: '#667085', lineHeight: 1.7 }}>
        {description}
      </Typography>
      <Button
        component={RouterLink}
        to={path}
        variant='text'
        sx={{
          mt: 1,
          px: 0,
          textTransform: 'none',
          fontWeight: 900,
          color: '#1f5fd1',
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  );
}

const subtleLabel = {
  color: '#667085',
  fontSize: '0.82rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const infoText = {
  mt: 0.8,
  color: '#111827',
  lineHeight: 1.75,
};

const ctaPrimary = {
  px: 3.2,
  py: 1.15,
  borderRadius: '999px',
  textTransform: 'none',
  fontWeight: 900,
  backgroundColor: '#1f5fd1',
  '&:hover': { backgroundColor: '#184ca8' },
};

const ctaDark = {
  px: 3.2,
  py: 1.15,
  borderRadius: '999px',
  textTransform: 'none',
  fontWeight: 900,
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.28)',
  '&:hover': {
    borderColor: '#fff',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
};

export default Contact;
